import { ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LucideIcon } from "lucide-react";

interface BrandCardProps {
  name: string;
  tagline: string;
  description: string;
  badge: string;
  badgeColor: "green" | "blue";
  highlights: { icon: LucideIcon; label: string }[];
  ctaText: string;
  ctaLink: string;
  accentColor: string;
}

const BrandCard = ({
  name,
  tagline,
  description,
  badge,
  badgeColor,
  highlights,
  ctaText,
  ctaLink,
  accentColor,
}: BrandCardProps) => {
  const isGreen = badgeColor === "green";
  
  return (
    <div className={`group relative p-8 rounded-2xl bg-card/80 backdrop-blur-sm border border-border transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 overflow-hidden ${isGreen ? 'hover:shadow-eco-green/20' : 'hover:shadow-sky-blue/20'}`}>
      {/* Animated gradient border effect */}
      <div 
        className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          background: `linear-gradient(135deg, ${accentColor}30 0%, transparent 50%, ${accentColor}20 100%)`,
        }}
      />
      
      {/* Animated glow orb */}
      <div 
        className="absolute -top-20 -right-20 w-40 h-40 rounded-full blur-3xl opacity-0 group-hover:opacity-30 transition-all duration-700"
        style={{ backgroundColor: accentColor }}
      />

      <div className="relative z-10">
        {/* Badge */}
        <span
          className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold mb-4 ${
            isGreen
              ? "bg-gradient-to-r from-eco-green/20 to-teal-bright/20 text-eco-green border border-eco-green/30"
              : "bg-gradient-to-r from-sky-blue/20 to-purple/20 text-sky-blue border border-sky-blue/30"
          }`}
        >
          <span className={`w-2 h-2 rounded-full ${isGreen ? 'bg-eco-green animate-pulse' : 'bg-sky-blue animate-pulse'}`} />
          {badge}
        </span>

        {/* Brand Name */}
        <h3
          className="text-2xl font-bold mb-1 transition-all duration-300 group-hover:scale-[1.02]"
          style={{ color: accentColor }}
        >
          {name}
        </h3>
        <p className="text-sm text-muted-foreground mb-4 font-medium">{tagline}</p>

        {/* Description */}
        <p className="text-muted-foreground mb-6 leading-relaxed">
          {description}
        </p>

        {/* Highlights */}
        <div className="flex flex-wrap gap-3 mb-6">
          {highlights.map((highlight, index) => (
            <div
              key={index}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm border transition-all duration-300 group-hover:scale-105 ${
                isGreen 
                  ? 'bg-eco-green/10 border-eco-green/20 hover:bg-eco-green/20' 
                  : 'bg-sky-blue/10 border-sky-blue/20 hover:bg-sky-blue/20'
              }`}
              style={{ transitionDelay: `${index * 50}ms` }}
            >
              <highlight.icon className={`w-4 h-4 ${isGreen ? 'text-eco-green' : 'text-sky-blue'}`} />
              <span className="text-foreground font-medium">{highlight.label}</span>
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <Button
          asChild
          className={`w-full group/btn font-semibold transition-all duration-300 hover:scale-[1.02] ${
            isGreen 
              ? 'bg-gradient-to-r from-eco-green to-teal-bright hover:from-eco-green-light hover:to-teal-bright text-white shadow-lg shadow-eco-green/25'
              : 'bg-gradient-to-r from-sky-blue to-purple hover:from-sky-blue-light hover:to-purple text-white shadow-lg shadow-sky-blue/25'
          }`}
        >
          <a href={ctaLink} target="_blank" rel="noopener noreferrer">
            {ctaText}
            <ExternalLink className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 group-hover/btn:-translate-y-0.5 transition-transform" />
          </a>
        </Button>
      </div>
    </div>
  );
};

export default BrandCard;
